import { Controller } from "@hotwired/stimulus";

// Privacy-friendly aggregate map for /cities: one proportional bubble per city
// showing the number of professionals there (no individual pins/profiles — that's
// /map). Each bubble links to /find/in/:city. Leaflet is the vendored window.L
// (no CDN), same as locals_map_controller. Data is handed in server-side as a
// JSON array value (also served at /cities.json).
export default class extends Controller {
  static values = {
    // [{ city, lat, lng, count, url }]
    cities: Array,
    // Center of the Netherlands — fallback when there are no bubbles.
    fallbackCenter: { type: Array, default: [52.1326, 5.2913] },
    fallbackZoom: { type: Number, default: 7 },
  };

  connect() {
    if (typeof L === "undefined") return;
    if (this.map) return;

    this.map = L.map(this.element);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(this.map);

    const cities = (this.citiesValue || []).filter(
      (c) => c.lat != null && c.lng != null && c.count > 0
    );
    const maxCount = cities.reduce((m, c) => Math.max(m, c.count), 1);
    const bounds = [];

    cities.forEach((c) => {
      const size = this.bubbleSize(c.count, maxCount);
      const font = Math.max(11, Math.round(size * 0.36));
      const icon = L.divIcon({
        className: "locals-citybubble-wrap",
        html:
          `<a class="locals-citybubble" href="${this.escape(c.url)}" ` +
          `style="width:${size}px;height:${size}px;font-size:${font}px" ` +
          `title="${this.escape(c.city)}: ${c.count}">${c.count}</a>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      L.marker([c.lat, c.lng], { icon }).addTo(this.map);
      bounds.push([c.lat, c.lng]);
    });

    if (bounds.length > 1) {
      this.map.fitBounds(bounds, { padding: [40, 40] });
    } else if (bounds.length === 1) {
      this.map.setView(bounds[0], 9);
    } else {
      this.map.setView(this.fallbackCenterValue, this.fallbackZoomValue);
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  // Diameter in px. Area scales with count (diameter with sqrt), normalised to
  // the busiest city and clamped to a sane on-screen range.
  bubbleSize(count, maxCount) {
    const MIN = 34;
    const MAX = 84;
    const t = Math.sqrt(count) / Math.sqrt(maxCount); // 0..1
    return Math.round(MIN + (MAX - MIN) * t);
  }

  escape(value) {
    if (value == null) return "";
    const span = document.createElement("span");
    span.textContent = value.toString();
    return span.innerHTML;
  }
}
